import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { useApiClient } from '@/api/ApiClientProvider'
import type { CampaignDetail } from '@/api/campaigns'
import type { Envelope } from '@/api/types'
import type { FormatOptions } from '@/design/format'
import { useCurrentTenant } from '@/lib/access'
import { filtersKey, useGlobalFilters } from '@/lib/useGlobalFilters'

const CAMPAIGNS_KEY = 'campaigns'

/** The tenant's currency and locale, for every money figure on these pages. */
export function useMoney(): FormatOptions {
  const tenant = useCurrentTenant()
  return { currency: tenant.data?.currency ?? 'GBP', locale: tenant.data?.locale ?? 'en-GB' }
}

export function useCampaignWebsites() {
  const client = useApiClient()
  return useQuery({
    queryKey: [CAMPAIGNS_KEY, client.kind, 'websites'],
    queryFn: () => client.campaignWebsites(),
    staleTime: 5 * 60_000,
  })
}

/** Shares its cache with the global filter bar's own query — same key, same data. */
export function useFilterOptions() {
  const client = useApiClient()
  return useQuery({
    queryKey: ['filter-options', client.kind],
    queryFn: () => client.filterOptions(),
  })
}

export function useCampaigns() {
  const client = useApiClient()
  const { filters } = useGlobalFilters()
  return useQuery({
    queryKey: [CAMPAIGNS_KEY, client.kind, 'list', filtersKey(filters)],
    queryFn: () => client.campaigns(filters),
    placeholderData: keepPreviousData,
  })
}

export function useCampaign(id: string | undefined) {
  const client = useApiClient()
  return useQuery({
    queryKey: [CAMPAIGNS_KEY, client.kind, 'detail', id],
    queryFn: () => client.campaign(id as string),
    enabled: Boolean(id),
  })
}

export function useCampaignPerformance(id: string | undefined, enabled = true) {
  const client = useApiClient()
  const { filters } = useGlobalFilters()
  return useQuery({
    queryKey: [CAMPAIGNS_KEY, client.kind, 'performance', id, filtersKey(filters)],
    queryFn: () => client.campaignPerformance(id as string, filters),
    enabled: Boolean(id) && enabled,
    placeholderData: keepPreviousData,
  })
}

export function useQrSvg(campaignId: string, trackingId: string, enabled: boolean) {
  const client = useApiClient()
  return useQuery({
    queryKey: [CAMPAIGNS_KEY, client.kind, 'qr', campaignId, trackingId],
    queryFn: () => client.campaignQrSvg(campaignId, trackingId),
    enabled,
    staleTime: Number.POSITIVE_INFINITY,
  })
}

/**
 * A campaign write. Every write answers with the campaign as it now stands,
 * which replaces the cached detail at once; the list and figures refetch.
 */
export function useCampaignMutation<Args extends unknown[]>(
  mutate: (...args: Args) => Promise<Envelope<CampaignDetail>>,
) {
  const queryClient = useQueryClient()
  const client = useApiClient()
  return useMutation({
    mutationFn: (args: Args) => mutate(...args),
    onSuccess: (result) => {
      const id = result.data.campaign.id
      queryClient.setQueryData([CAMPAIGNS_KEY, client.kind, 'detail', id], result)
      void queryClient.invalidateQueries({ queryKey: [CAMPAIGNS_KEY, client.kind, 'list'] })
      void queryClient.invalidateQueries({ queryKey: [CAMPAIGNS_KEY, client.kind, 'performance', id] })
    },
  })
}

/** A refusal's message when it has one worth showing, else `fallback`. */
export function errorMessage(error: unknown, fallback: string): string {
  return error instanceof Error && error.message && error.name !== 'TypeError' ? error.message : fallback
}
